import { useState, useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useSite, usePublic } from "../../lib/site";
import { Img, WorksPageHeader } from "../../components/site/Common";
import { Seo, breadcrumbLd } from "../../components/site/Seo";
import { stripHtml, href } from "../../lib/i18n";
import { lazyImgs } from "../../lib/html";
import NotFound, { Loading } from "./NotFound";
import { ShareButtons } from "../../components/site/Cards";
import ServiceInquiryModal from "../../components/site/ServiceInquiryModal";
import ServiceBlocksList, { CaseCard, TestimonialCard, BlockRenderer } from "../../components/site/ServiceBlocks";

export default function ServiceDetail() {
  const { slug } = useParams();
  const loc = useLocation();
  // Keep ?preview=1 behavior for iframe-based preview, but no edit mode anymore.
  useMemo(() => new URLSearchParams(loc.search), [loc.search]);

  const { lang, t, site } = useSite();
  const { data: s, isLoading, isError } = usePublic(["public-service", slug], `/public/services/${slug}`);
  const [modalOpen, setModalOpen] = useState(false);

  if (isLoading) return <Loading />;
  if (isError || !s) return <NotFound />;

  const title = t(s.title);
  const desc = stripHtml(t(s.description)).slice(0, 155);
  const highlights = s.highlights || [];
  const cases = s.cases || [];
  const quotes = s.testimonials || [];
  const blocks = Array.isArray(s.content_blocks) ? s.content_blocks : [];
  const hasBlocks = blocks.length > 0;
  const types = new Set(blocks.map((b) => b?.type));
  const hasDescBlock = types.has("description");
  const hasHighlightsBlock = types.has("highlights");
  const hasCompanyBlock = types.has("company");
  const company = s.company || {};
  const hasCompanyData = !!(company.name || company.logo || company.address || company.phone || company.email || company.website || company.map_url || company.map_embed);
  const openCta = () => setModalOpen(true);
  const heroHeight = Number(s.hero_height || 0);
  const heroStyle = heroHeight > 0 ? { height: `${heroHeight}px` } : {};
  const heroImgCls = heroHeight > 0
    ? "w-full h-full object-cover rounded-2xl border border-site-border"
    : "w-full aspect-[21/9] object-cover rounded-2xl border border-site-border";
  const heroImgStyle = {
    objectPosition: `${Math.max(0, Math.min(100, Number(s.cover_pos_x ?? 50)))}% ${Math.max(0, Math.min(100, Number(s.cover_pos_y ?? 50)))}%`,
  };
  const hasDescText = !!(t(s.description) || "").trim();
  const hasAnyTopContent = hasDescText || highlights.length > 0;
  const showDefaultTop = !hasDescBlock && !hasHighlightsBlock && hasAnyTopContent;

  return (
    <article data-testid="service-detail-page" className="pb-24 sm:pb-32">
      <Seo
        title={`${title} — ${t(site.general?.site_name) || "Olgu Tansuğ"}`}
        description={desc}
        image={s.cover}
        jsonLd={breadcrumbLd([
          { name: t(site.general?.site_name) || "", path: href(lang, "home") },
          { name: lang === "tr" ? "Çalışmalarım" : "My Work", path: "/calismalarim" },
          { name: title, path: `/calismalarim/${s.slug}` },
        ])}
      />

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24">
        <WorksPageHeader
          eyebrow={lang === "tr" ? "Çalışmalarım" : "My Work"}
          title={title}
          backLink={(
            <Link to="/calismalarim" className="inline-flex items-center gap-1.5 text-sm text-s-muted hover:text-s-primary transition-colors mb-6" data-testid="service-back-link">
              <ArrowLeft size={14} /> {lang === "tr" ? "Tüm hizmetler" : "All services"}
            </Link>
          )}
          share={<ShareButtons title={title} testid="service-share-button" kind="service" shareUrl={`${window.location.origin}/api/public/share/service/${s.slug}?v=${encodeURIComponent((s.updated_at || "").replace(/[^0-9]/g, "").slice(0, 12) || "1")}`} />}
        />
      </header>

      {s.cover && (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-10" style={heroStyle}>
          <Img src={s.cover} alt={title} className={heroImgCls} style={heroImgStyle} />
        </div>
      )}

      {/* Default top (description full width + highlights) — hidden when corresponding blocks exist. */}
      {showDefaultTop && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-12 space-y-10">
          {hasDescText && (
            <div className="prose-site prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: lazyImgs(t(s.description) || "") }} />
          )}
          {highlights.length > 0 && (
            <div className="bg-s-surface rounded-2xl border border-site-border p-6">
              <p className="eyebrow mb-4">{lang === "tr" ? "Öne çıkan başlıklar" : "Highlights"}</p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
                {highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-s-primary shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
              {s.cta_label && t(s.cta_label) && (
                <button type="button" onClick={openCta} className="mt-6 inline-flex items-center gap-1.5 bg-s-primary text-s-onprimary rounded-full px-5 py-2.5 text-sm font-semibold hover:brightness-110 transition" data-testid="service-cta">
                  {t(s.cta_label)} <ArrowRight size={14} />
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {/* If only description block NOT used but highlights block IS used, render raw description above blocks */}
      {!hasDescBlock && hasHighlightsBlock && t(s.description) && (
        <section className="max-w-3xl mx-auto px-5 sm:px-8 mt-12">
          <div className="prose-site prose-lg" dangerouslySetInnerHTML={{ __html: lazyImgs(t(s.description) || "") }} />
        </section>
      )}

      {/* Dynamic content blocks (page-builder output) — read-only */}
      {hasBlocks && (
        <ServiceBlocksList
          blocks={blocks}
          t={t}
          fallbackCases={cases}
          fallbackRefs={quotes}
          service={s}
          onCta={openCta}
          editable={false}
        />
      )}

      {/* Legacy static sections — only render if no blocks defined */}
      {!hasBlocks && cases.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-20">
          <div className="flex items-end justify-between gap-4 mb-7">
            <h2 className="font-heading text-3xl font-bold">{lang === "tr" ? "Seçili çalışmalar" : "Selected work"}</h2>
            <span className="text-sm text-s-muted">{cases.length}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {cases.map((c) => <CaseCard key={c.id || c.title?.tr} c={c} t={t} />)}
          </div>
        </section>
      )}

      {!hasBlocks && quotes.length > 0 && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 mt-20">
          <h2 className="font-heading text-3xl font-bold mb-7">{lang === "tr" ? "Referanslar" : "References"}</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {quotes.map((q) => <TestimonialCard key={q.id || q.name} q={q} t={t} />)}
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 mt-24">
        {!hasCompanyBlock && hasCompanyData && (
          <div
            className="mb-4 rounded-2xl border border-white/20 px-5 sm:px-6 py-4 flex items-center gap-4 flex-wrap sm:flex-nowrap"
            style={{ background: "rgba(248,244,232,0.08)", backdropFilter: "blur(6px)" }}
            data-testid="service-company-slim"
          >
            {company.logo && (
              <div
                className="shrink-0 w-14 h-14 rounded-full overflow-hidden flex items-center justify-center"
                style={{ boxShadow: "0 0 0 1px rgba(255,255,255,0.08), 0 0 24px rgba(212,175,55,0.18), 0 0 48px rgba(255,255,255,0.06)" }}
              >
                <Img src={company.logo} alt={company.name || ""} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              {company.name && (
                <p className="font-heading text-base sm:text-lg font-semibold leading-tight" style={{ color: "var(--s-primary)" }}>
                  {company.name}
                </p>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-[13px] text-white/75">
                {company.address && <span className="truncate max-w-full">{company.address}</span>}
                {company.phone && <a href={`tel:${company.phone}`} className="hover:text-white transition">{company.phone}</a>}
                {company.email && <a href={`mailto:${company.email}`} className="hover:text-white transition">{company.email}</a>}
                {company.website && (
                  <a href={company.website} target="_blank" rel="noopener noreferrer" className="hover:text-white transition inline-flex items-center gap-1">
                    {company.website.replace(/^https?:\/\//, "").replace(/\/$/, "")} <ArrowRight size={11} className="rotate-[-45deg]" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="bg-black/80 rounded-2xl border border-white/10 p-10 sm:p-12 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.22em] mb-4" style={{ color: "var(--s-primary)" }}>Birlikte Çalışalım</p>
          <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:whitespace-nowrap">Bu Alanda Desteğe mi İhtiyacınız Var?</h3>
          <p className="text-white/60 mb-7 max-w-xl mx-auto">İhtiyaçlarınızı birlikte değerlendirelim, size en uygun çözümü belirleyelim.</p>
          <button type="button" onClick={openCta} className="inline-flex items-center gap-2 bg-s-primary text-s-onprimary rounded-full px-7 py-3 font-semibold hover:brightness-110 transition" data-testid="service-bottom-cta">
            Birlikte Değerlendirelim <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <ServiceInquiryModal open={modalOpen} onClose={() => setModalOpen(false)} serviceTitle={title} serviceSlug={s.slug} />
    </article>
  );
}
